/**
 * Окно статуса выгрузки: карточки МП в разрезе кабинета.
 *
 * Окно — представление реестра выгрузок, а не владелец операции: закрытие
 * ничего не останавливает, повторное открытие из списка фоновых задач
 * показывает то же содержимое.
 *
 * Состояние задачи окно берёт из реестра, а не напрямую из хранилища фоновых
 * задач: оттуда завершённая задача исчезает через пять секунд после финального
 * события, и окно, читавшее только её, откатывалось к «задача ещё не
 * подтвердила запуск» с нулевым прогрессом — ровно то, на чём 08.08.2026
 * зависла живая выгрузка на OZON. Наполняет реестр `usePublishJobFeed`:
 * события плюс опрос состояния.
 */

import { useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock, Database, RefreshCw } from 'lucide-react'
import { hasPermission } from '../../app/access'
import { useAuthStore } from '../../stores/authStore'
import { Badge, type BadgeVariant } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { usePublishJobsStore } from './publishJobsStore'
import {
  buildStatusGroups,
  JOB_STATUS_LABEL,
  marketplaceLabel,
  plural,
  publishScopeLabel,
  recordArticleCount,
  statusTotals,
} from './publishScope'
import type { PublishStatusRow } from './publishScope'
import { PUBLISH_JOB_HANDSHAKE_TIMEOUT_MS, usePublishJobFeed } from './usePublishJobFeed'
import { publishCardsAPI, publishRequestError } from './api'

const NO_PERMISSIONS: string[] = []

/**
 * Подписи итоговых статусов карточки. Каталоги ведут свои карты бейджей для
 * своих таблиц; здесь нужен ещё и «итог не пришёл», которого там нет.
 */
const RESULT_STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  draft: { label: 'Черновик PIM', variant: 'gray' },
  creating: { label: 'Отправляется', variant: 'yellow' },
  created: { label: 'Создана на МП', variant: 'blue' },
  price_set: { label: 'Цена установлена', variant: 'green' },
  stock_set: { label: 'Остаток проставлен', variant: 'green' },
  selling: { label: 'В продаже', variant: 'green' },
  skipped: { label: 'Пропущена', variant: 'gray' },
  error: { label: 'Отказ', variant: 'red' },
}

function statusBadge(row: PublishStatusRow) {
  if (row.state === 'error') return { label: 'Отказ', variant: 'red' as BadgeVariant }
  if (!row.status) return { label: 'Ожидает', variant: 'gray' as BadgeVariant }
  return RESULT_STATUS[row.status] || { label: row.status, variant: 'gray' as BadgeVariant }
}

/**
 * Куда делась карточка при отказе. Отказ до отправки на площадку оставляет её
 * в прежнем состоянии, и человеку важно знать, что повторить выгрузку можно.
 */
function residualStatusLabel(row: PublishStatusRow): string | null {
  if (row.state !== 'error') return null
  const status = String(row.status ?? '').toLowerCase()
  if (!status || status === 'error') return null
  return RESULT_STATUS[status]?.label || row.status
}

const HANDSHAKE_SECONDS = Math.round(PUBLISH_JOB_HANDSHAKE_TIMEOUT_MS / 1000)

interface PublishStatusModalProps {
  jobId: string | null
  isOpen: boolean
  onClose: () => void
}

export function PublishStatusModal({ jobId, isOpen, onClose }: PublishStatusModalProps) {
  const record = usePublishJobsStore((state) => (jobId ? state.records[jobId] : undefined))
  const applyMergeRetry = usePublishJobsStore((state) => state.applyMergeRetry)
  const permissions = useAuthStore((state) => state.user?.permissions ?? NO_PERMISSIONS)
  const canRetryMerge = hasPermission('Card:UPDATE', permissions)
  const [retryingCardId, setRetryingCardId] = useState<number | null>(null)
  const [retryError, setRetryError] = useState<{ cardId: number; message: string } | null>(null)
  const retryLock = useRef(false)
  const { snapshot, finished, handshakeOverdue, refreshing, refresh } = usePublishJobFeed(
    jobId,
    isOpen,
  )

  const groups = useMemo(() => buildStatusGroups(record ?? null), [record])
  const totals = useMemo(() => statusTotals(groups), [groups])
  const articleCount = recordArticleCount(record ?? null)
  const catalogFreshness = snapshot?.catalogFreshness ?? []
  const catalogFreshnessEnforced = snapshot?.catalogFreshnessEnforced !== false

  const retryMerge = async (row: PublishStatusRow): Promise<void> => {
    if (!jobId || !finished || retryLock.current || row.cardId === null) return
    retryLock.current = true
    setRetryingCardId(row.cardId)
    setRetryError(null)
    try {
      const result = await publishCardsAPI.retryMarketplaceCardMerge([row.cardId])
      const fresh = result.results.find((item) => item.card_id === row.cardId)
      if (!fresh) throw new Error('Сервер не вернул свежий итог склейки карточки')
      applyMergeRetry(jobId, fresh)
      if (fresh.merge_status === 'failed') throw new Error('Wildberries снова отказал в склейке')
    } catch (error: any) {
      setRetryError({
        cardId: row.cardId,
        message: publishRequestError(error, 'Не удалось повторить склейку'),
      })
    } finally {
      retryLock.current = false
      setRetryingCardId(null)
    }
  }

  if (!isOpen || !jobId) return null

  const percentage = snapshot?.percentage ?? 0
  const jobFailed = snapshot?.status === 'failed'
  const jobHalted = snapshot?.status === 'cancelled' || snapshot?.status === 'stopped'
  const headline = snapshot
    ? snapshot.currentStep || JOB_STATUS_LABEL[snapshot.status]
    : 'Задача ещё не подтвердила запуск'

  const subtitle = record?.restored
    ? `Состав восстановлен по итогам задачи · карточек: ${totals.total}`
    : record
      ? `${publishScopeLabel(record.scope)} · товаров: ${articleCount} · карточек: ${totals.total}`
      : 'Состав выгрузки недоступен: запись о запуске не сохранилась'

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Выгрузка карточек на маркетплейсы"
      subtitle={subtitle}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Закрыть
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-gray-700 dark:text-zinc-300">{headline}</span>
            <span className="tabular-nums text-gray-500 dark:text-zinc-400">
              {snapshot ? `${percentage}%` : '—'}
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-zinc-700">
            <div
              className={
                totals.failed > 0 || jobFailed
                  ? 'h-full bg-red-500 transition-all'
                  : finished
                    ? 'h-full bg-green-500 transition-all'
                    : 'h-full bg-indigo-500 transition-all'
              }
              style={{ width: `${Math.min(100, percentage)}%` }}
            />
          </div>
          <p
            className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-2xs text-gray-500 dark:text-zinc-400"
            aria-label="Итоги выгрузки"
          >
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
              Готово: <b className="tabular-nums">{totals.done}</b>
            </span>
            <span className="inline-flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
              Отказов: <b className="tabular-nums">{totals.failed}</b>
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 text-gray-400" />
              Ожидает: <b className="tabular-nums">{totals.pending}</b>
            </span>
          </p>
        </div>

        {snapshot?.bundles && (
          <p
            aria-label="Итоги склейки"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 dark:border-zinc-700 dark:text-zinc-300"
          >
            Собрано склеек: <b className="tabular-nums">{snapshot.bundles.created}</b>,{' '}
            перенесено номенклатур: <b className="tabular-nums">{snapshot.bundles.topped_up}</b>,{' '}
            не перенеслось: <b className="tabular-nums">{snapshot.bundles.failed_cards}</b>
          </p>
        )}

        {catalogFreshness.length > 0 && (
          <div className="space-y-2" aria-label="Свежесть каталогов маркетплейсов">
            {catalogFreshness.map((freshness) => {
              const staleState = freshness.state === 'stale' || freshness.state === 'unknown'
              const blocked = freshness.blocks_real_publish
              const softened = !catalogFreshnessEnforced && staleState
              const warning = freshness.state === 'warning' || softened
              const age = freshness.age_ms === null
                ? 'успешная синхронизация ещё не подтверждена'
                : freshness.age_ms < 60 * 60 * 1000
                  ? `${Math.max(1, Math.floor(freshness.age_ms / 60_000))} мин назад`
                  : `${Math.floor(freshness.age_ms / 3_600_000)} ч назад`
              return (
                <div
                  key={`${freshness.marketplace}:${freshness.seller_id}`}
                  role={blocked || softened ? 'alert' : undefined}
                  className={blocked
                    ? 'flex gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200'
                    : warning
                      ? 'flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'
                      : 'flex gap-3 rounded-lg border border-gray-200 p-3 text-sm text-gray-700 dark:border-zinc-700 dark:text-zinc-300'}
                >
                  <Database className="mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-semibold">
                      Каталог {marketplaceLabel(freshness.marketplace)} · кабинет {freshness.seller_id}
                    </p>
                    <p className="mt-0.5">
                      Данные каталога подтверждены: {age}.
                      {blocked
                        ? ' Реальная выгрузка заблокирована. Обновите каталог на странице площадки и повторите выгрузку.'
                        : softened
                          ? ' Запрет по свежести отключён настройкой: реальная выгрузка разрешена несмотря на риск. Проверка состояния и барьер занятости артикула продолжают работать.'
                          : freshness.state === 'warning'
                          ? ' Каталог скоро потребует обновления.'
                          : ' Проверка занятости артикулов использует свежие данные.'}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Задача не подтвердила запуск: молчать об этом нельзя — человек ждёт */}
        {handshakeOverdue && (
          <div
            role="alert"
            className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="space-y-2">
              <p>
                Задача не подтвердила запуск за {HANDSHAKE_SECONDS}{' '}
                {plural(HANDSHAKE_SECONDS, ['секунду', 'секунды', 'секунд'])}. Выгрузка могла не
                начаться. Проверьте задачу в списке фоновых задач, и если её там нет — повторите
                выгрузку. Окно можно закрыть: на серверную операцию это не влияет.
              </p>
              <Button
                variant="secondary"
                size="sm"
                icon={<RefreshCw className="h-3.5 w-3.5" />}
                loading={refreshing}
                onClick={refresh}
              >
                Проверить снова
              </Button>
            </div>
          </div>
        )}

        {/* Отказ самой задачи: до карточек дело могло и не дойти */}
        {jobFailed && (
          <div
            role="alert"
            className="flex gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">Задача завершилась отказом</p>
              <p className="mt-0.5 break-words">
                {snapshot?.error || 'Причина отказа не передана сервером'}
              </p>
            </div>
          </div>
        )}

        {jobHalted && (
          <p className="rounded-lg border border-gray-200 p-3 text-sm text-amber-700 dark:border-zinc-700 dark:text-amber-300">
            {JOB_STATUS_LABEL[snapshot!.status]}
            {snapshot?.error ? `: ${snapshot.error}` : '. Часть карточек могла остаться необработанной.'}
          </p>
        )}

        {groups.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            Состав выгрузки недоступен. Прогресс задачи виден в виджете фоновых задач.
          </p>
        ) : (
          <div className="space-y-3" aria-label="Карточки в разрезе кабинета">
            {groups.map((group) => (
              <section
                key={group.key}
                className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700"
                aria-label={`Кабинет ${group.cabinet}`}
              >
                <h4 className="flex items-baseline justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-100">
                  <span>
                    {marketplaceLabel(group.marketplace)} · кабинет {group.cabinet}
                  </span>
                  <span className="text-2xs font-normal tabular-nums text-gray-500 dark:text-zinc-400">
                    {group.rows.length}
                  </span>
                </h4>
                <table className="w-full text-left text-sm">
                  <thead className="text-2xs uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                    <tr>
                      <th className="px-3 py-1.5 font-semibold">Артикул</th>
                      <th className="px-3 py-1.5 font-semibold">Площадка</th>
                      <th className="px-3 py-1.5 font-semibold">Склейка</th>
                      <th className="px-3 py-1.5 font-semibold">Состояние</th>
                      <th className="px-3 py-1.5 font-semibold">Причина отказа</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row) => {
                      const badge = statusBadge(row)
                      const residual = residualStatusLabel(row)
                      return (
                        <tr
                          key={row.key}
                          className="border-t border-gray-100 align-top dark:border-zinc-800"
                        >
                          <td className="px-3 py-1.5 font-mono text-[0.8125rem] text-gray-900 dark:text-zinc-100">
                            {row.article}
                          </td>
                          <td className="px-3 py-1.5 text-gray-700 dark:text-zinc-300">
                            {marketplaceLabel(row.marketplace)}
                          </td>
                          <td className="px-3 py-1.5 text-[0.8125rem] text-gray-700 dark:text-zinc-300">
                            {row.mergeLabel && (
                              <span className="block font-medium text-gray-900 dark:text-zinc-100">
                                {row.mergeLabel}
                              </span>
                            )}
                            {row.mergeStatus === 'merged' ? (
                              <span className="text-2xs text-green-700 dark:text-green-400">
                                Собрана{row.imtId ? ` · imtID ${row.imtId}` : ''}
                              </span>
                            ) : row.mergeStatus === 'skipped_disabled' ? (
                              <span className="text-2xs text-gray-500 dark:text-zinc-400">
                                Склейка выключена
                              </span>
                            ) : row.mergeStatus === 'failed' ? (
                              <div className="space-y-1">
                                <span className="block text-2xs text-red-600 dark:text-red-400">
                                  Не перенеслась
                                </span>
                                {finished && canRetryMerge && row.cardId !== null && (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    loading={retryingCardId === row.cardId}
                                    disabled={retryingCardId !== null && retryingCardId !== row.cardId}
                                    aria-label={`Повторить склейку карточки ${row.article}`}
                                    onClick={() => void retryMerge(row)}
                                  >
                                    Повторить склейку
                                  </Button>
                                )}
                                {retryError?.cardId === row.cardId && (
                                  <span role="alert" className="block text-2xs text-red-600 dark:text-red-400">
                                    {retryError.message}
                                  </span>
                                )}
                              </div>
                            ) : row.mergeStatus === 'not_applicable' ? (
                              <span className="text-2xs text-gray-500 dark:text-zinc-400">
                                Не применяется
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-zinc-600">—</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5">
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                            {residual && (
                              <span className="mt-0.5 block text-2xs text-gray-500 dark:text-zinc-400">
                                карточка осталась: {residual}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-[0.8125rem]">
                            {row.error || row.code ? (
                              <span className="text-red-600 dark:text-red-400">
                                {row.error || 'Отказ без пояснения'}
                                {row.code && (
                                  <span className="ml-1 font-mono text-2xs text-gray-500 dark:text-zinc-400">
                                    {row.code}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-zinc-600">—</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </section>
            ))}
          </div>
        )}

        <p className="text-2xs text-gray-500 dark:text-zinc-400">
          Окно можно закрыть — выгрузка продолжится на сервере. Вернуться к ней можно из списка
          фоновых задач.
        </p>
      </div>
    </Modal>
  )
}

export default PublishStatusModal
